import React, { useState, useEffect, useContext } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TableSortLabel,
  Container,
  Toolbar,
  Switch,
  FormControlLabel,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CustomerDetailsModal from './CustomerDetailsModal';
import { getComparator, Order } from '../utils';
import axios from 'axios';
import ExcelDownloadButton from '../ExcelDownloadButton';
import { useAuth0 } from '@auth0/auth0-react';
import { AppContext } from '../../AppContext';

export interface DataRow {
  id: string;
  customerName: string;
  accountNumber?: string | null;
  regionName: string;
  totalDevices: number;
  activatedAndInstalled: number;
  autoActivated: number;
  notActive: number;
  terminated: number;
  suspended: number;
  maxDaysAged: number;
  percentageInstalled: string;
  comments: string;
  active: boolean;
}

type DataKey = keyof DataRow;
type Alignment = 'inherit' | 'left' | 'center' | 'right' | 'justify';

interface HeadCell {
  id: DataKey;
  label: string;
  align?: Alignment;
  tooltip: string;
}

const headCells: HeadCell[] = [
  {
    id: 'customerName',
    label: 'Customer Name',
    align: 'left',
    tooltip: 'Name of the customer',
  },
  {
    id: 'totalDevices',
    label: 'Total Devices',
    align: 'right',
    tooltip: 'Total number of devices in MyAdmin',
  },
  {
    id: 'activatedAndInstalled',
    label: 'Activated & Installed',
    align: 'right',
    tooltip: 'Devices that are activated and installed',
  },
  {
    id: 'autoActivated',
    label: 'Auto Activated',
    align: 'right',
    tooltip: 'Devices that are auto activated',
  },
  {
    id: 'notActive',
    label: 'Not Active',
    align: 'right',
    tooltip: 'Devices that are not active',
  },
  {
    id: 'terminated',
    label: 'Terminated',
    align: 'right',
    tooltip: 'Devices that have been terminated',
  },
  {
    id: 'suspended',
    label: 'Suspended',
    align: 'right',
    tooltip: 'Devices that are suspended',
  },
  {
    id: 'maxDaysAged',
    label: 'Max Days Aged',
    align: 'right',
    tooltip: 'Days Aged for the oldest device that has yet to be installed',
  },
  {
    id: 'percentageInstalled',
    label: '% Installed',
    align: 'right',
    tooltip: 'Percentage of devices installed (Activated & Installed / Total Devices)',
  },
];

const DeviceStatusTable: React.FC = () => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<DataKey>('customerName');
  const { user: auth0User } = useAuth0();
  const { user: appUser } = useContext(AppContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<DataRow | null>(null);
  const [customers, setCustomers] = useState<DataRow[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const regionId = appUser?.region_id;
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/customers`, {
          params: { region_id: regionId },
        });

        const transformedData: DataRow[] = response.data.map((customer: any) => {
          const totalDevices = customer.num_devices_in_myadmin || 0;
          const activatedAndInstalled = customer.total_activated_and_installed_devices || 0;
          return {
            id: customer.id.toString(),
            customerName: customer.name,
            accountNumber: customer.account_number ?? null,
            regionName: customer.region_name,
            totalDevices: totalDevices,
            activatedAndInstalled: activatedAndInstalled,
            autoActivated: customer.total_auto_activated_devices || 0,
            notActive: totalDevices - activatedAndInstalled,
            terminated: customer.total_terminated_devices || 0,
            suspended: customer.total_suspended_devices || 0,
            maxDaysAged: customer.devices_max_days_aged || 0,
            percentageInstalled: customer.percent_devices_installed
              ? `${parseFloat(customer.percent_devices_installed).toFixed(2)}%`
              : '0.00%',
            comments: customer.comments || '',
            active: customer.active,
          };
        });
        setCustomers(transformedData);
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };

    fetchCustomers();
  }, [auth0User?.region_id, appUser?.region_id]);

  const updateCustomerComments = async (customerId: string, newComments: string) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/update-customer`, {
        customer_id: customerId,
        current_auth0_sub: auth0User?.sub,
        comments: newComments,
      });
      setCustomers((prevCustomers) =>
        prevCustomers.map((customer) =>
          customer.id === customerId ? { ...customer, comments: newComments } : customer
        )
      );
    } catch (error) {
      console.error('Error updating comments:', error);
    }
  };

  const handleRequestSort = (property: DataKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const excelData = customers.map((customer) => ({
    Customer: customer.customerName,
    'Account Number': customer.accountNumber || '',
    Region: customer.regionName,
    'Total Devices': customer.totalDevices,
    'Activated & Installed': customer.activatedAndInstalled,
    'Auto Activated': customer.autoActivated,
    'Not Active': customer.notActive,
    Terminated: customer.terminated,
    Suspended: customer.suspended,
    'Max Days Aged': customer.maxDaysAged,
    '% Installed': customer.percentageInstalled,
    Comments: customer.comments,
  }));

  // Filter customers based on search query and the active toggle.
  const filteredRows = React.useMemo(() => {
    const q = searchQuery.toLowerCase();

    return customers.filter((customer) => {
      const matchesSearch =
        customer.customerName.toLowerCase().includes(q) ||
        (customer.accountNumber ?? '').toLowerCase().includes(q);

      const matchesToggle = showInactive ? !customer.active : customer.active;
      return matchesSearch && matchesToggle;
    });
  }, [customers, searchQuery, showInactive]);

  const sortedRows = React.useMemo(() => {
    if (orderBy === 'percentageInstalled') {
      return [...filteredRows].sort((a, b) => {
        const aVal = parseFloat(a.percentageInstalled.replace('%', ''));
        const bVal = parseFloat(b.percentageInstalled.replace('%', ''));
        return order === 'asc' ? aVal - bVal : bVal - aVal;
      });
    } else {
      return [...filteredRows].sort(getComparator(order, orderBy));
    }
  }, [filteredRows, order, orderBy]);

  return (
    <Container maxWidth="xl">
      <Toolbar />
      <Grid container alignItems="center" justifyContent="space-between" marginBottom={2}>
        <Grid>
          <TextField
            label="Search by Name or Account Number"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: '250px' }}
          />
        </Grid>
        <Grid container alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            }
            label={showInactive ? "Inactive Accounts" : "Active Accounts"}
          />
          <ExcelDownloadButton data={excelData} filename="device_status.xlsx" />
        </Grid>
      </Grid>
      <TableContainer component={Paper} sx={{ maxHeight: '500px', overflowY: 'auto' }}>
        <Table stickyHeader aria-label="device-status-table">
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell key={headCell.id} align={headCell.align}>
                  <Tooltip title={headCell.tooltip}>
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length > 0 ? (
              sortedRows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.14)" },
                  }}
                  onClick={() => {
                    setSelectedCustomer(row);
                    setIsModalOpen(true);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">
                        {row.customerName}
                      </Typography>
                      {row.accountNumber && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.25 }}
                        >
                          {row.accountNumber}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{row.totalDevices}</TableCell>
                  <TableCell align="right">{row.activatedAndInstalled}</TableCell>
                  <TableCell align="right">{row.autoActivated}</TableCell>
                  <TableCell align="right">{row.notActive}</TableCell>
                  <TableCell align="right">{row.terminated}</TableCell>
                  <TableCell align="right">{row.suspended}</TableCell>
                  <TableCell align="right">{row.maxDaysAged}</TableCell>
                  <TableCell align="right">{row.percentageInstalled}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No matching records found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedCustomer && (
        <CustomerDetailsModal
          customer={selectedCustomer}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          updateComments={updateCustomerComments}
        />
      )}
    </Container>
  );
};

export default DeviceStatusTable;
